"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useAnimationFrame, useReducedMotion } from "framer-motion";
import { ROUTES } from "@/src/constants/routes";

import type { HomeFeaturedProduct } from "@/src/features/products/types";

const fallbackImage = "/Office%20Furniture/image-removebg-preview.png";
const LOOP_COPIES = 4;
const START_COPY_INDEX = 1;
const AUTO_SCROLL_SPEED = 42;

type CategoryProductsCarouselProps = {
  categoryId: number;
  categoryName: string;
  products: HomeFeaturedProduct[];
  autoScrollDirection?: "from-right" | "from-left";
};

function getProductHref(product: HomeFeaturedProduct) {
  return ROUTES.PRODUCT(product.slug);
}

export default function CategoryProductsCarousel({
  categoryId,
  categoryName,
  products,
  autoScrollDirection = "from-right",
}: CategoryProductsCarouselProps) {
  const prefersReducedMotion = useReducedMotion();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const firstGroupRef = useRef<HTMLDivElement | null>(null);
  const sequenceWidthRef = useRef(0);
  const offsetRef = useRef(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    const firstGroup = firstGroupRef.current;

    if (!viewport || !firstGroup || products.length === 0) {
      return undefined;
    }

    const updateMetrics = () => {
      const sequenceWidth = firstGroup.offsetWidth;

      if (sequenceWidth <= 0) {
        return;
      }

      sequenceWidthRef.current = sequenceWidth;

      const centeredOffset =
        autoScrollDirection === "from-right"
          ? sequenceWidth * START_COPY_INDEX
          : sequenceWidth * (START_COPY_INDEX + 1);

      offsetRef.current = centeredOffset;
      viewport.scrollLeft = centeredOffset;
    };

    updateMetrics();

    const resizeObserver = new ResizeObserver(() => {
      updateMetrics();
    });

    resizeObserver.observe(viewport);
    resizeObserver.observe(firstGroup);

    return () => {
      resizeObserver.disconnect();
    };
  }, [autoScrollDirection, products]);

  useAnimationFrame((_, delta) => {
    if (prefersReducedMotion) {
      return;
    }

    const viewport = viewportRef.current;
    const sequenceWidth = sequenceWidthRef.current;

    if (!viewport || sequenceWidth <= 0) {
      return;
    }

    const minimumOffset = sequenceWidth * START_COPY_INDEX;
    const maximumOffset = sequenceWidth * (START_COPY_INDEX + 1);
    const pixelsToMove = AUTO_SCROLL_SPEED * (delta / 1000);

    let nextOffset =
      autoScrollDirection === "from-right"
        ? offsetRef.current + pixelsToMove
        : offsetRef.current - pixelsToMove;

    if (autoScrollDirection === "from-right") {
      if (nextOffset >= maximumOffset) {
        nextOffset -= sequenceWidth;
      }

      if (nextOffset < minimumOffset) {
        nextOffset = minimumOffset;
      }
    } else {
      if (nextOffset <= minimumOffset) {
        nextOffset += sequenceWidth;
      }

      if (nextOffset > maximumOffset) {
        nextOffset = maximumOffset;
      }
    }

    offsetRef.current = nextOffset;
    viewport.scrollLeft = nextOffset;
  });

  if (products.length === 0) {
    return (
      <div className="flex min-h-[320px] flex-col justify-between rounded-[2rem] border border-[var(--color-line)] bg-white/75 p-6 text-right backdrop-blur-xl">
        <div className="space-y-3" dir="rtl">
          <p className="text-xs font-bold tracking-[0.22em] text-[var(--color-ink-muted)]">
            CURATED PRODUCTS
          </p>
          <h3 className="text-2xl font-bold text-[var(--color-ink)]">
            منتجات {categoryName} قريبًا
          </h3>
          <p className="max-w-2xl text-sm leading-7 text-[var(--color-ink-soft)]">
            نجهز الآن تشكيلة هذا القسم بشكل أفضل. يمكنك الانتقال لصفحة المنتجات
            الكاملة ومتابعة كل الإضافات الجديدة أولاً بأول.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--color-line)] pt-5">
          <span className="text-sm text-[var(--color-ink-soft)]" dir="rtl">
            سيتم تحديث هذا القسم تلقائيًا عند إضافة منتجات جديدة.
          </span>
          <Link
            href={`/products?category=${categoryId}&direct=1`}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-5 text-sm font-bold text-[var(--color-ink)] transition hover:-translate-y-0.5 hover:border-[var(--color-accent)]"
          >
            عرض كل المنتجات
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-hidden rounded-[2rem] bg-white/72 backdrop-blur-xl">
      <div
        ref={viewportRef}
        className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ direction: "ltr" }}
      >
        <div className="flex w-max">
          {Array.from({ length: LOOP_COPIES }).map((_, copyIndex) => (
            <div
              key={copyIndex}
              ref={copyIndex === 0 ? firstGroupRef : undefined}
              className="flex flex-none gap-3 pe-3 last:pe-3 sm:gap-4 sm:pe-4"
            >
              {products.map((product, productIndex) => (
                <article
                  key={`${copyIndex}-${product.id}-${product.sku}-${productIndex}`}
                  className="group min-w-[170px] max-w-[170px] flex-none overflow-hidden rounded-[1.3rem] border border-[var(--color-line)] bg-white/92 sm:min-w-[210px] sm:max-w-[210px] sm:rounded-[1.45rem] md:min-w-[240px] md:max-w-[240px] md:rounded-[1.65rem] lg:min-w-[260px] lg:max-w-[260px]"
                >
                  <Link
                    href={getProductHref(product)}
                    className="block"
                    aria-label={`عرض المنتج ${product.title}`}
                  >
                    <div className="relative h-[110px] overflow-hidden bg-[#f7f2ed] sm:h-[260px] md:h-[300px] lg:h-[360px]">
                      <Image
                        src={product.image || fallbackImage}
                        alt={product.title}
                        fill
                        sizes="(min-width: 1024px) 260px, (min-width: 768px) 240px, (min-width: 640px) 210px, 170px"
                        className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                      />
                      <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/5" />
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
